import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  ChevronLeft,
  ChevronRight,
  Search,
  FileText,
  Play,
  BookOpen,
  Loader2
} from 'lucide-react'

interface ContentItem {
  id: string
  title: string
  content_number: number
  has_video: boolean
  has_audio: boolean
  has_theory: boolean
  lesson: {
    id: string
    title: string
    order_num: number
    unit: {
      id: string
      title: string
      order_num: number
    }
  }
}

export default function ContentsList() {
  const [contents, setContents] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null)

  useEffect(() => {
    loadContents()
  }, [])

  const loadContents = async () => {
    const { data } = await supabase
      .from('contents')
      .select(`
        id, title, content_number, video_url, audio_url, theory_content,
        lesson:lessons(id, title, order_num, unit:units(id, title, order_num))
      `)
      .eq('is_active', true)
      .order('content_number', { ascending: true })

    if (data) {
      setContents(data.map((c: any) => ({
        id: c.id,
        title: c.title,
        content_number: c.content_number,
        has_video: !!c.video_url,
        has_audio: !!c.audio_url,
        has_theory: !!c.theory_content,
        lesson: c.lesson
      })))
    }
    setLoading(false)
  }

  const filtered = contents.filter(c =>
    c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.lesson?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.lesson?.unit?.title?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Agrupar por unidad
  const byUnit = filtered.reduce<Record<string, { unit: ContentItem['lesson']['unit'], lessons: Record<string, { lesson: ContentItem['lesson'], contents: ContentItem[] }> }>>((acc, content) => {
    const unitId = content.lesson?.unit?.id
    const lessonId = content.lesson?.id
    if (!unitId || !lessonId) return acc

    if (!acc[unitId]) {
      acc[unitId] = { unit: content.lesson.unit, lessons: {} }
    }
    if (!acc[unitId].lessons[lessonId]) {
      acc[unitId].lessons[lessonId] = { lesson: content.lesson, contents: [] }
    }
    acc[unitId].lessons[lessonId].contents.push(content)
    return acc
  }, {})

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/teacher/content" className="p-2 hover:bg-muted rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Contenidos</h1>
          <p className="text-muted-foreground">
            {contents.length} contenidos disponibles · Haz clic en "Ver" para previsualizar
          </p>
        </div>
      </div>

      {/* Buscador */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Buscar por título, lección o unidad..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background"
        />
      </div>

      {/* Contenidos agrupados por unidad */}
      <div className="space-y-4">
        {Object.values(byUnit)
          .sort((a, b) => a.unit.order_num - b.unit.order_num)
          .map(({ unit, lessons }) => {
            const isOpen = expandedUnit === unit.id || !!searchTerm
            return (
              <div key={unit.id} className="bg-card rounded-xl border overflow-hidden">
                {/* Header unidad */}
                <button
                  onClick={() => setExpandedUnit(isOpen && !searchTerm ? null : unit.id)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left"
                >
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs text-muted-foreground">Unidad {unit.order_num}</span>
                    <p className="font-semibold text-gray-900">{unit.title}</p>
                  </div>
                  <span className="text-xs text-muted-foreground mr-2">
                    {Object.values(lessons).reduce((sum, l) => sum + l.contents.length, 0)} contenidos
                  </span>
                  <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </button>

                {/* Lecciones y contenidos */}
                {isOpen && (
                  <div className="border-t divide-y">
                    {Object.values(lessons)
                      .sort((a, b) => a.lesson.order_num - b.lesson.order_num)
                      .map(({ lesson, contents: lessonContents }) => (
                        <div key={lesson.id} className="px-4 py-3">
                          <p className="text-sm font-medium text-blue-600 mb-2">
                            Lección {lesson.order_num}: {lesson.title}
                          </p>
                          <div className="space-y-2 ml-2">
                            {lessonContents
                              .sort((a, b) => a.content_number - b.content_number)
                              .map((content) => (
                                <div
                                  key={content.id}
                                  className="flex items-center justify-between p-3 bg-muted/40 rounded-lg"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
                                      <FileText className="w-4 h-4 text-orange-600" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        Contenido {content.content_number}: {content.title}
                                      </p>
                                      <div className="flex gap-2 mt-0.5">
                                        {content.has_video && (
                                          <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">Video</span>
                                        )}
                                        {content.has_audio && (
                                          <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">Audio</span>
                                        )}
                                        {content.has_theory && (
                                          <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded">Teoría</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <Link
                                    to={`/teacher/preview/content/${content.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors shrink-0"
                                  >
                                    <Play className="w-3.5 h-3.5" />
                                    Ver
                                  </Link>
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )
          })}

        {Object.keys(byUnit).length === 0 && (
          <div className="text-center py-12 bg-card rounded-xl border">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium">No se encontraron contenidos</p>
          </div>
        )}
      </div>
    </div>
  )
}
